import { useState } from "react";
import { Star } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useT } from "@/lib/i18n";
import { Link } from "react-router-dom";
import { toast } from "sonner";

export function Stars({ value, size = 14, className = "text-gold" }) {
    return (
        <div className={`inline-flex items-center gap-0.5 ${className}`} aria-label={`${value} out of 5`}>
            {[1, 2, 3, 4, 5].map((n) => (
                <Star
                    key={n}
                    size={size}
                    fill={n <= Math.round(value) ? "currentColor" : "none"}
                    stroke="currentColor"
                />
            ))}
        </div>
    );
}

export function StarsInput({ value, onChange }) {
    const [hover, setHover] = useState(0);
    return (
        <div className="inline-flex gap-1" data-testid="review-stars-input">
            {[1, 2, 3, 4, 5].map((n) => (
                <button
                    type="button"
                    key={n}
                    onClick={() => onChange(n)}
                    onMouseEnter={() => setHover(n)}
                    onMouseLeave={() => setHover(0)}
                    data-testid={`review-star-${n}`}
                    aria-label={`${n} star`}
                    className="text-terracotta hover:scale-110 transition-transform"
                >
                    <Star size={28} fill={n <= (hover || value) ? "currentColor" : "none"} stroke="currentColor" />
                </button>
            ))}
        </div>
    );
}

export function ReviewForm({ cabinId, onSubmitted }) {
    const { user } = useAuth();
    const { t } = useT();
    const [rating, setRating] = useState(0);
    const [title, setTitle] = useState("");
    const [body, setBody] = useState("");
    const [submitting, setSubmitting] = useState(false);

    if (!user) {
        return (
            <div className="border border-espresso/15 p-6 bg-bone2/40" data-testid="review-cta-signin">
                <div className="eyebrow mb-2">{t("cabinDetail.reviewForm.stayedHere")}</div>
                <p className="text-sm text-muted2 mb-4">
                    <Link to="/login" className="underline text-forest hover:text-terracotta">{t("cabinDetail.reviewForm.signIn")}</Link> {t("cabinDetail.reviewForm.signInPrompt")}
                </p>
            </div>
        );
    }

    const submit = async (e) => {
        e.preventDefault();
        if (rating < 1) { toast.error(t("toast.ratingNeeded")); return; }
        if (body.trim().length < 10) { toast.error(t("toast.reviewBodyShort")); return; }
        setSubmitting(true);
        try {
            await api.post("/reviews", { cabin_id: cabinId, rating, title, body });
            toast.success(t("toast.reviewSubmitted"));
            setRating(0); setTitle(""); setBody("");
            onSubmitted?.();
        } catch (err) {
            const detail = err.response?.data?.detail || t("toast.reviewError");
            toast.error(detail);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form onSubmit={submit} className="border border-espresso/15 p-6 lg:p-8 bg-bone2/30" data-testid="review-form">
            <div className="eyebrow mb-3">{t("cabinDetail.reviewForm.eyebrow")}</div>
            <h3 className="font-chiller text-3xl mb-4">{t("cabinDetail.reviewForm.title")}</h3>
            <p className="text-xs text-muted2 mb-6" data-testid="review-gating-note">{t("cabinDetail.mustComplete")}</p>
            <div className="mb-5">
                <label className="eyebrow block mb-2">{t("cabinDetail.reviewForm.rating")}</label>
                <StarsInput value={rating} onChange={setRating} />
            </div>
            <div className="mb-5">
                <label className="eyebrow block mb-2">{t("cabinDetail.reviewForm.titleField")}</label>
                <input data-testid="review-title" value={title} onChange={(e) => setTitle(e.target.value)} className="w-full bg-transparent border-b border-espresso/30 focus:border-forest outline-none py-3" />
            </div>
            <div className="mb-6">
                <label className="eyebrow block mb-2">{t("cabinDetail.reviewForm.bodyField")}</label>
                <textarea data-testid="review-body" value={body} onChange={(e) => setBody(e.target.value)} required rows={5} className="w-full bg-transparent border-b border-espresso/30 focus:border-forest outline-none py-3" />
            </div>
            <button type="submit" disabled={submitting} data-testid="review-submit" className="px-6 py-3 bg-forest text-bone text-xs uppercase tracking-widest2 font-semibold hover:bg-forestDark disabled:opacity-50">
                {submitting ? "Submitting…" : t("cabinDetail.reviewForm.submit")}
            </button>
            <p className="mt-3 text-xs text-muted2">{t("cabinDetail.reviewForm.moderated")}</p>
        </form>
    );
}
